import Fuse from 'fuse.js';
import { Cities, GetClosestCitiesQuery } from 'types/generated/client';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';

type Position = {
  latitude: number;
  longitude: number;
};

type City = GetClosestCitiesQuery['cities'][0];

export function findBestCityMatch(
  queryString: string | null | undefined,
  cities: City[],
  position: Position,
) {
  // Input validation
  if (
    !Array.isArray(cities) ||
    cities.length === 0 ||
    !position?.latitude ||
    !position?.longitude
  ) {
    return null;
  }

  // First, add distance to all cities and sort by proximity
  const citiesByDistance = cities
    .map((city) => {
      const distance = calculateHaversineDistance({
        coord1: position,
        coord2: {
          latitude: city.latitude,
          longitude: city.longitude,
        },
      });
      return {
        ...city,
        distance,
      };
    })
    .sort((a, b) => a.distance - b.distance);

  // If no query string provided, return the closest city
  if (!queryString || typeof queryString !== 'string') {
    const closestCity = citiesByDistance[0];
    console.log('No query string provided, returning closest city:', closestCity?.name);
    return closestCity || null;
  }

  // Method 1: Direct substring matching (most reliable for exact matches)
  const directMatch = citiesByDistance.find((city) =>
    queryString.toLowerCase().includes(city.name.toLowerCase()),
  );

  if (directMatch) {
    return directMatch;
  }

  // Method 2: Reverse fuzzy search - search each city name within the address
  const cityMatches = citiesByDistance
    .map((city) => {
      const queryWords = queryString.toLowerCase().split(/[\s,]+/);
      const cityName = city.name.toLowerCase();

      let bestScore = 1;

      queryWords.forEach((word) => {
        // Skip very short words
        if (word.length < 2) return;

        // Calculate similarity between query word and city name
        const fuse = new Fuse([word], {
          threshold: 0.4,
          includeScore: true,
        });

        const result = fuse.search(cityName);
        if (result.length > 0 && result?.[0]?.score && result[0].score < bestScore) {
          bestScore = result[0].score;
        }
      });

      return {
        ...city,
        fuzzyScore: bestScore,
      };
    })
    .filter((city) => city.fuzzyScore < 0.6) // Only include reasonable matches
    .sort((a, b) => a.fuzzyScore - b.fuzzyScore); // Sort by best fuzzy score

  // Use the best fuzzy match if found
  if (cityMatches.length > 0) {
    const bestFuzzyMatch = cityMatches[0];
    return bestFuzzyMatch;
  }

  // Method 3: Alternative approach - split query and search each part
  const queryParts = queryString.split(',').map((part) => part.trim());

  for (const part of queryParts) {
    // Skip empty parts
    if (!part) continue;

    const fuse = new Fuse(citiesByDistance, {
      keys: ['name'],
      threshold: 0.4,
      includeScore: true,
    });

    const partResults = fuse.search(part);
    if (partResults.length > 0) {
      return partResults[0].item;
    }
  }

  // Fallback to closest city if no matches found
  const closestCity = citiesByDistance[0];

  return closestCity || null;
}
